import React, { useMemo } from 'react';
import { Box } from '@interstate/components/Box';
import { TextInput } from '@interstate/components/TextInput';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import { Bars3Icon } from '@interstate/components/Icons/Bars3Icon';

import {
    TableBody as InterstateTableBody,
    TableCell,
    TableHead as InterstateTableHead,
    TableRow
} from '@interstate/components/SimpleTable';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { type FieldConfigurationKey, TEXT_INPUT_MAX_LENGTH } from './constants';
import { getFieldLabel, getToolTipContent } from './utils';
import styles from './GeneralTab.module.css';
import dragStyles from '../../common/DragAndDropTable.module.css';
import { Typography } from '@interstate/components/Typography';
import { DragAndDropTable } from '../../common/DragAndDropTable';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Tooltip } from '@interstate/components/Tooltip';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

interface TableItem {
    fieldKey: FieldConfigurationKey;
    displayName: string;
    order: number;
}

interface TableColumn {
    heading: JSX.Element | string;
    className: string;
}

interface GeneralTabProps {
    fieldConfiguration: Record<FieldConfigurationKey, { displayName: string; bold: boolean; active: boolean; order: number }>;
    onDisplayNameChange: (fieldKey: FieldConfigurationKey, value: string) => void;
    onBoldChange: (fieldKey: FieldConfigurationKey, checked: boolean) => void;
    onActiveChange: (fieldKey: FieldConfigurationKey, checked: boolean) => void;
    onOrderChange: (orderedKeys: FieldConfigurationKey[]) => void;
}

const GeneralTabBase: React.FC<GeneralTabProps> = ({
    fieldConfiguration,
    onDisplayNameChange,
    onBoldChange,
    onActiveChange,
    onOrderChange
}) => {
    const theme = useInterstateTheme();

    const tableData: TableItem[] = useMemo(
        () =>
            Object.entries(fieldConfiguration)
                .map(([fieldKey, config]) => ({
                    fieldKey: fieldKey as FieldConfigurationKey,
                    displayName: getFieldLabel(fieldKey as FieldConfigurationKey),
                    order: config.order
                }))
                .sort((a, b) => a.order - b.order),
        [fieldConfiguration]
    );

    const activeCount = Object.values(fieldConfiguration).filter((config) => config.active).length;

    const tableColumns: TableColumn[] = [
        { heading: '', className: styles.dragHandleHeader },
        { heading: 'Field', className: styles.fieldHeader },
        {
            heading: (
                <Box display="flex" alignItems="center" gap="4px">
                    Display Name <InformationCircleIcon type="info" />
                </Box>
            ),
            className: styles.displayNameHeader
        },
        { heading: 'Bold Text', className: styles.boldHeader },
        { heading: `Active (${activeCount})`, className: styles.activeHeader }
    ];

    const renderRow = (item: TableItem, index: number) => {
        const config = fieldConfiguration[item.fieldKey];

        const getCheckedFromCheckboxEvent = (e: InterstateOnChangeEvent<CheckBoxEventValue>) => e.target.value.checked;

        const handleBoldChange = (e: InterstateOnChangeEvent<CheckBoxEventValue>) => {
            const nextBold = getCheckedFromCheckboxEvent(e);

            if (nextBold && !config.active) {
                onActiveChange(item.fieldKey, true);
            }

            onBoldChange(item.fieldKey, nextBold);
        };

        const handleActiveChange = (e: InterstateOnChangeEvent<CheckBoxEventValue>) => {
            const nextActive = getCheckedFromCheckboxEvent(e);

            onActiveChange(item.fieldKey, nextActive);

            if (!nextActive && config.bold) {
                onBoldChange(item.fieldKey, false);
            }
        };

        const toolTipContent = getToolTipContent(item.fieldKey);

        return [
            <TableCell key={`drag-${index}`} className={dragStyles.dragHandleCell}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    data-role="drag-handle"
                    className={dragStyles.dragHandleBox}
                >
                    <Bars3Icon data-testid={`drag-handle-${item.fieldKey}`} />
                </Box>
            </TableCell>,
            <TableCell key={`field-${index}`} className={styles.fieldCell}>
                <Box display="inline-flex" alignItems="center" gap={1}>
                    {item.displayName}
                    {toolTipContent && (
                        <Tooltip size="lg" position="bottom" toolTipContent={toolTipContent}>
                            <InformationCircleIcon
                                color={theme.tokens.BaseColorBlue500}
                                data-testid={`${item.fieldKey}-info-icon`}
                            />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>,
            <TableCell key={`display-${index}`} className={styles.displayNameCell}>
                <Box>
                    <TextInput
                        displayLabel={false}
                        name={`display-name-${item.fieldKey}`}
                        value={config.displayName}
                        onChange={(e) => onDisplayNameChange(item.fieldKey, e.target.value)}
                        size="small"
                        required
                        maxLength={TEXT_INPUT_MAX_LENGTH}
                        customErrorMessages={{
                            exceedsMaxLength: `Value exceeds ${TEXT_INPUT_MAX_LENGTH} characters.`
                        }}
                        data-testid={`display-name-input-${item.fieldKey}`}
                    />
                </Box>
            </TableCell>,
            <TableCell key={`bold-${index}`} className={styles.boldCell}>
                <Box display="flex" justifyContent="center">
                    <CheckBox
                        value={`bold-${item.fieldKey}`}
                        checked={config.bold}
                        onChange={handleBoldChange}
                        data-testid={`bold-checkbox-${item.fieldKey}`}
                    />
                </Box>
            </TableCell>,
            <TableCell key={`active-${index}`} className={styles.activeCell}>
                <Box display="flex" justifyContent="center">
                    <CheckBox
                        value={`active-${item.fieldKey}`}
                        checked={config.active}
                        onChange={handleActiveChange}
                        data-testid={`active-checkbox-${item.fieldKey}`}
                    />
                </Box>
            </TableCell>
        ];
    };

    const handleReorder = (reorderedItems: TableItem[]) => {
        const reorderedKeys = reorderedItems.map((item) => item.fieldKey);
        onOrderChange(reorderedKeys);
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" gap="8px" mt={2} mb={1}>
                <InformationCircleIcon type="info" />
                <Typography variant="body-md">Check boxes to select the fields to print by default.</Typography>
            </Box>

            <DragAndDropTable
                items={tableData}
                onReorder={handleReorder}
                dragHandleSelector="[data-role='drag-handle']"
                data-testid="field-configuration-table"
                dataDensity="large"
                hover={false}
            >
                <InterstateTableHead>
                    <TableRow>
                        {tableColumns.map(({ heading, className }, index) => (
                            <TableCell key={`head-cell-${index}`} className={className}>
                                <Box className={styles.headerBox}>{heading}</Box>
                            </TableCell>
                        ))}
                    </TableRow>
                </InterstateTableHead>
                <InterstateTableBody>
                    {tableData.map((item, index) => (
                        <TableRow key={item.fieldKey} data-field-key={item.fieldKey}>
                            {renderRow(item, index)}
                        </TableRow>
                    ))}
                </InterstateTableBody>
            </DragAndDropTable>
        </Box>
    );
};

export const GeneralTab = React.memo(GeneralTabBase);
